/**
 * Utility functions for authentication and authorization
 */

export interface UserPermissions {
  email: string;
  subscriberId: string;
  isAdmin: boolean;
  isVerified: boolean;
  role: string;
  permissions: string[];
}

/**
 * Decodes a JWT token and extracts user information and permissions
 * @param token The JWT token
 * @returns UserPermissions object or null if token is invalid
 */
export function decodeToken(token: string): UserPermissions | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload);
    const claims = JSON.parse(decodedPayload);

    return {
      email: claims.sub || claims.email,
      subscriberId: claims.subscriberId,
      isAdmin: claims.isAdmin || false,
      isVerified: claims.isVerified || false,
      role: claims.role || (claims.isAdmin ? 'ADMIN' : 'SUBSCRIBER'),
      permissions: claims.permissions || []
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Gets the current user's permissions from localStorage
 * @returns UserPermissions object or null if no valid token
 */
export function getCurrentUserPermissions(): UserPermissions | null {
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }
  return decodeToken(token);
}

/**
 * Checks if the current user has a specific permission
 * @param permission The permission to check
 * @returns true if user has the permission, false otherwise
 */
export function hasPermission(permission: string): boolean {
  const userPermissions = getCurrentUserPermissions();
  if (!userPermissions) {
    return false;
  }
  return userPermissions.permissions.includes(permission);
}

/**
 * Checks if the current user has all specified permissions
 * @param permissions Array of permissions to check
 * @returns true if user has all permissions, false otherwise
 */
export function hasAllPermissions(permissions: string[]): boolean {
  const userPermissions = getCurrentUserPermissions();
  if (!userPermissions) {
    return false;
  }
  return permissions.every(permission => userPermissions.permissions.includes(permission));
}

/**
 * Checks if the current user has any of the specified permissions
 * @param permissions Array of permissions to check
 * @returns true if user has any of the permissions, false otherwise
 */
export function hasAnyPermission(permissions: string[]): boolean {
  const userPermissions = getCurrentUserPermissions();
  if (!userPermissions) {
    return false;
  }
  return permissions.some(permission => userPermissions.permissions.includes(permission));
}

/**
 * Checks if the current user is an admin
 * @returns true if user is admin, false otherwise
 */
export function isAdmin(): boolean {
  const userPermissions = getCurrentUserPermissions();
  return userPermissions?.isAdmin || false;
}

/**
 * Checks if the current user is a subscriber
 * @returns true if user is subscriber, false otherwise
 */
export function isSubscriber(): boolean {
  const userPermissions = getCurrentUserPermissions();
  return userPermissions?.role === 'SUBSCRIBER';
}

/**
 * Checks if the current user is an agent
 * @returns true if user is agent, false otherwise
 */
export function isAgent(): boolean {
  const userPermissions = getCurrentUserPermissions();
  return userPermissions?.role === 'AGENT';
}

// Permission constants
export const PERMISSIONS = {
  // Company permissions
  COMPANY_READ: 'COMPANY_READ',
  COMPANY_CREATE: 'COMPANY_CREATE',
  COMPANY_UPDATE: 'COMPANY_UPDATE',
  COMPANY_DELETE: 'COMPANY_DELETE',

  // Client permissions
  CLIENT_READ: 'CLIENT_READ',
  CLIENT_CREATE: 'CLIENT_CREATE',
  CLIENT_UPDATE: 'CLIENT_UPDATE',
  CLIENT_DELETE: 'CLIENT_DELETE',

  // Invoice permissions
  INVOICE_READ: 'INVOICE_READ',
  INVOICE_CREATE: 'INVOICE_CREATE',
  INVOICE_UPDATE: 'INVOICE_UPDATE',
  INVOICE_DELETE: 'INVOICE_DELETE',

  // Agent management permissions
  AGENT_READ: 'AGENT_READ',
  AGENT_CREATE: 'AGENT_CREATE',
  AGENT_UPDATE: 'AGENT_UPDATE',
  AGENT_DELETE: 'AGENT_DELETE',

  // Admin permissions
  ADMIN_ACCESS: 'ADMIN_ACCESS',
  USER_MANAGEMENT: 'USER_MANAGEMENT',

  // Dashboard permissions
  DASHBOARD_ACCESS: 'DASHBOARD_ACCESS',

  // Profile permissions
  PROFILE_READ: 'PROFILE_READ',
  PROFILE_UPDATE: 'PROFILE_UPDATE',

  // Order permissions
  ORDER_READ: 'ORDER_READ',
  ORDER_CREATE: 'ORDER_CREATE',
  ORDER_UPDATE: 'ORDER_UPDATE',
  ORDER_DELETE: 'ORDER_DELETE',

  // Supplier permissions
  SUPPLIER_READ: 'SUPPLIER_READ',
  SUPPLIER_CREATE: 'SUPPLIER_CREATE',
  SUPPLIER_UPDATE: 'SUPPLIER_UPDATE',
  SUPPLIER_DELETE: 'SUPPLIER_DELETE',

  // Feature permissions
  STOCK_FEATURE: 'STOCK_FEATURE'
} as const;
